/**
 * F14.2 — «MIS TRABAJOS» CON LOS DATOS DE ESTE DISPOSITIVO
 *
 * Sin conexión, «Mis trabajos» enseña exactamente las mismas tarjetas que en línea: este módulo traduce el paquete
 * local (OT, líneas, documentos) y la cola de operaciones a la forma de `GET /api/mis-ots` (`MiTrabajo`,
 * `MiTrabajoDetalle`). No es un segundo producto: es otra fuente para la misma pantalla.
 *
 * El estado de cada tarea es el PROYECTADO (lo que dijo el servidor al descargar + las acciones guardadas aquí que
 * aún no llegaron: `proyeccion.ts`); el del servidor nunca se sobrescribe. Lo que el paquete no sabe (incidencias
 * abiertas) sale a cero: sin conexión no hay forma honesta de saberlo.
 */
import type { MiTarea, MiTrabajo, MiTrabajoDetalle } from '../api.ts';
import {
  leerOtLocal,
  listarLineasDeOtsLocal,
  listarMutacionesDeOt,
  listarMutacionesNoSincronizadas,
  listarOtsLocal,
  type DocumentoOfflineLocal,
  type LineaOfflineLocal,
  type Mutacion,
  type OtOfflineLocal,
} from './db.ts';
import { lineaIniciadaEnDispositivo } from './caducidad.ts';
import { presentarEstado, type PresentacionEstado } from './estados-sync.ts';
import { esMutacionActiva, esMutacionDeLinea, proyectarEstadoLinea } from './proyeccion.ts';

const HECHAS = new Set(['CONFIRMADA', 'VERIFICADA', 'NO_OK', 'NO_APLICA']);

function estadoProyectado(linea: LineaOfflineLocal, operaciones: readonly Mutacion[]): string {
  const activas = operaciones.filter((m) => m.idEntidad === linea.id && esMutacionDeLinea(m) && esMutacionActiva(m));
  return proyectarEstadoLinea(linea.estado, activas);
}

function aTarea(linea: LineaOfflineLocal, operaciones: readonly Mutacion[]): MiTarea {
  return {
    id: linea.id,
    orden: linea.orden,
    ubicacionCodigo: linea.ubicacionCodigo,
    ubicacionRuta: linea.ubicacionRuta,
    ubicacionDescripcion: linea.ubicacionDescripcion ?? linea.ubicacionCodigo,
    descripcionTarea: linea.descripcionTarea,
    tipoCodigo: linea.tipoCodigo,
    tipoDescripcion: linea.tipoDescripcion,
    procedimiento: linea.procedimiento ?? null,
    incidenciasAbiertas: 0,
    estado: estadoProyectado(linea, operaciones),
    materialesPrevistos: linea.materialesPrevistos ?? [],
    revisionBase: linea.revisionBase,
    tieneNc: linea.tieneNc ?? false,
    ncAsociada: linea.ncAsociada ?? null,
  };
}

function aTrabajo(ot: OtOfflineLocal, tareas: readonly MiTarea[]): MiTrabajo {
  return {
    id: ot.id,
    codigo: ot.codigo,
    estado: ot.estado,
    fechaPlan: ot.inicioProgramadoEn.slice(0, 10),
    fechaLimite: ot.fechaLimite,
    centroCodigo: ot.centroCodigo,
    centroDescripcion: ot.centroDescripcion,
    alcance: ot.alcanceOperario ?? 'TODA_OT',
    inicioProgramadoEn: ot.inicioProgramadoEn,
    duracionMin: ot.duracionMin ?? 0,
    fechaLocal: ot.inicioProgramadoEn.slice(0, 10),
    salaDescripcion: ot.salaDescripcion,
    misTareas: {
      total: tareas.length,
      hechas: tareas.filter((t) => HECHAS.has(t.estado)).length,
      noConformes: tareas.filter((t) => t.estado === 'NO_OK').length,
    },
    incidenciasAbiertas: 0,
  };
}

/** La jornada guardada en el dispositivo, en el orden en que se va a hacer. */
export async function leerJornadaLocal(perfilId: string): Promise<MiTrabajo[]> {
  const [ots, lineas, operaciones] = await Promise.all([listarOtsLocal(perfilId), listarLineasDeOtsLocal(perfilId), listarMutacionesNoSincronizadas(perfilId)]);
  return ots
    .sort((a, b) => a.inicioProgramadoEn.localeCompare(b.inicioProgramadoEn))
    .map((ot) => aTrabajo(ot, lineas.filter((l) => l.idOt === ot.id).map((l) => aTarea(l, operaciones))));
}

export interface TrabajoLocal {
  readonly detalle: MiTrabajoDetalle;
  readonly documentos: readonly DocumentoOfflineLocal[];
  /** Tareas (líneas) iniciadas en este dispositivo — ver `caducidad.ts`: por LÍNEA, determinista. */
  readonly tareasIniciadas: ReadonlySet<string>;
}

export async function leerTrabajoLocal(perfilId: string, idOt: string): Promise<TrabajoLocal | null> {
  const [local, operaciones] = await Promise.all([leerOtLocal(perfilId, idOt), listarMutacionesDeOt(perfilId, idOt)]);
  if (local === null) return null;
  const tareas = local.lineas.map((l) => aTarea(l, operaciones));
  return {
    detalle: { ...aTrabajo(local.ot, tareas), observaciones: local.ot.observaciones, lineas: tareas, documentos: local.documentos },
    documentos: local.documentos,
    tareasIniciadas: new Set(local.lineas.filter((l) => lineaIniciadaEnDispositivo(l, operaciones.filter((m) => m.idEntidad === l.id))).map((l) => l.id)),
  };
}

/** Estado de sincronización de la ÚLTIMA acción de cada tarea que aún no está en el servidor (para su tarjeta). */
export async function estadoColaPorTarea(perfilId: string, idOt: string): Promise<ReadonlyMap<string, PresentacionEstado>> {
  const operaciones = await listarMutacionesDeOt(perfilId, idOt);
  const mapa = new Map<string, PresentacionEstado>();
  for (const m of operaciones) {
    if (m.estadoSync === 'SINCRONIZADA') continue;
    const idLinea = esMutacionDeLinea(m) ? m.idEntidad : m.tipo === 'EVIDENCIA_SUBIR' && (m.payload as { ambito: string }).ambito === 'LINEA' ? m.idEntidad : null;
    if (idLinea !== null) mapa.set(idLinea, presentarEstado(m));
  }
  return mapa;
}

/**
 * En línea, el servidor manda; pero una acción guardada en el dispositivo y aún sin sincronizar tiene que verse en su
 * tarjeta (con su «pendiente de sincronizar»), no desaparecer hasta que llegue. Se proyecta encima, nunca se borra.
 */
export async function proyectarColaSobre(perfilId: string, detalle: MiTrabajoDetalle): Promise<MiTrabajoDetalle> {
  const operaciones = (await listarMutacionesDeOt(perfilId, detalle.id)).filter((m) => esMutacionDeLinea(m) && esMutacionActiva(m));
  if (operaciones.length === 0) return detalle;
  return {
    ...detalle,
    lineas: detalle.lineas.map((t) => ({ ...t, estado: proyectarEstadoLinea(t.estado, operaciones.filter((m) => m.idEntidad === t.id)) })),
  };
}
